import { CircleInfo } from "@/icons/CircleInfo.tsx";
import api from "@/lib/api.ts";
import { TagsResponse } from "@/models/tag.model.ts";
import { TemplateModel, TemplatesResponse } from "@/models/template.model.ts";
import { Error } from "@/ui/shared/Error.tsx";
import { Loading } from "@/ui/shared/Loading.tsx";
import { TemplateCard } from "@/ui/shared/TemplateCard.tsx";
import { Alert, Chip } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export function Templates() {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [templates, setTemplates] = useState<TemplateModel[]>();
  const templatesParams = {
    limit: 20,
    sort: "-created_at",
    "filter[tags]": selectedTags.join(","),
    include: "tags",
  };

  const {
    status: templatesStatus,
    data: templatesData,
    isLoading,
  } = useQuery({
    queryKey: ["templates", templatesParams],
    queryFn: () => api.get("templates", { params: templatesParams }),
    retry: 1,
  });

  useEffect(() => {
    if (templatesData) {
      const templatesResponse: TemplatesResponse = templatesData?.data || [];
      setTemplates(templatesResponse.data);
    }
  }, [templatesData]);

  const tagsParams = {
    limit: 500,
    sort: "name",
  };

  const { data: tagsData } = useQuery({
    queryKey: ["tags", tagsParams],
    queryFn: () =>
      api.get<TagsResponse>("tags", {
        params: tagsParams,
      }),
    retry: 1,
  });
  const tagsResponse: TagsResponse | undefined = tagsData?.data;
  const tags: string[] = tagsResponse?.data.map((t) => t.name) || [];

  if (templatesStatus === "error") {
    return <Error />;
  }

  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {tags?.map((tag) => (
          <Chip
            size="xs"
            key={tag}
            checked={selectedTags.includes(tag)}
            onChange={() => toggleTag(tag)}
          >
            {tag}
          </Chip>
        ))}
      </div>
      {templates?.length === 0 && templatesStatus === "success" && (
        <Alert
          bd="1px dashed"
          color="grape"
          icon={<CircleInfo size={20} />}
          className="w-full"
        >
          No photos found!
        </Alert>
      )}
      {isLoading && <Loading />}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
        {templates?.map((template) => (
          <TemplateCard key={template.id} template={template} />
        ))}
      </div>
    </>
  );
}
